/**
 * Member statistics utility functions
 *
 * Helper functions for calculating member performance statistics
 * from team competition format summary data.
 */

import type { TeamCompetitorFormatSummary } from '../types/api.types';

/**
 * Format statistics for a single member
 */
export interface FormatStats {
  format: string;
  variation: string | null | undefined;
  played: number;
  won: number;
  halved: number;
  lost: number;
  points: number;
}

/**
 * Total statistics across all formats
 */
export interface FormatTotals {
  won: number;
  halved: number;
  lost: number;
  played: number;
  points: number;
}

/**
 * Calculate played games from win/halve/loss counts
 *
 * @param won - Number of wins
 * @param halved - Number of halved games
 * @param lost - Number of losses
 * @returns Total games played
 */
export const calculatePlayed = (won: number, halved: number, lost: number): number => {
  return won + halved + lost;
};

/**
 * Calculate points from wins and halves (wins = 1 point, halves = 0.5 points)
 *
 * @param won - Number of wins
 * @param halved - Number of halved games
 * @returns Total points scored
 */
export const calculatePoints = (won: number, halved: number): number => {
  return won + halved / 2;
};

/**
 * Process format summary data into formatted statistics
 *
 * @param formatSummary - Array of format summaries from API
 * @returns Array of formatted statistics with calculated fields
 */
export const processFormatStats = (formatSummary: TeamCompetitorFormatSummary[]): FormatStats[] => {
  return formatSummary.map((format) => {
    const won = format.won || 0;
    const halved = format.halved || 0;
    const lost = format.lost || 0;

    return {
      format: format.format || '',
      variation: format.variation,
      played: calculatePlayed(won, halved, lost),
      won,
      halved,
      lost,
      points: calculatePoints(won, halved),
    };
  });
};

/**
 * Calculate totals across all formats
 *
 * @param formatSummary - Array of format summaries from API
 * @returns Total statistics aggregated across all formats
 */
export const calculateFormatTotals = (formatSummary: TeamCompetitorFormatSummary[]): FormatTotals => {
  const totals = formatSummary.reduce(
    (accumulator, currValue) => ({
      won: accumulator.won + (currValue.won || 0),
      halved: accumulator.halved + (currValue.halved || 0),
      lost: accumulator.lost + (currValue.lost || 0),
    }),
    { won: 0, halved: 0, lost: 0 }
  );

  return {
    ...totals,
    played: calculatePlayed(totals.won, totals.halved, totals.lost),
    points: calculatePoints(totals.won, totals.halved),
  };
};
